import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { format, parse, isValid } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;

  // Try yyyy-MM-dd
  let parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return parsed;

  // Try MM/dd/yyyy
  parsed = parse(dateStr, "MM/dd/yyyy", new Date());
  if (isValid(parsed)) return parsed;

  // Try new Date
  const fallback = new Date(dateStr);
  if (isValid(fallback)) return fallback;

  return null;
};

const textFieldSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
    borderRadius: "8px",
    height: "43px",
    "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: disabled ? "#999893" : "#0e1324",
    pr: 0,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#999893",
    fontSize: "14px",
  },
});

const labelSx = {
  fontSize: "13px",
  color: "#66638C",
  fontWeight: 500,
  mb: "8px",
  display: "block",
};

const dashIfEmpty = (disabled, value) =>
  disabled && !value ? "-" : value || "";

const isEmptyNumeric = (value) =>
  value === undefined || value === "" || value === 0;

const counterSlotProps = (disabled, text, max) =>
  disabled
    ? undefined
    : {
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                component="span"
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  whiteSpace: "nowrap",
                  mr: 0.5,
                }}
              >
                {(text || "").length}/{max}
              </Typography>
            </InputAdornment>
          ),
        },
      };

const limitedChange = (onChange, field, max) => (e) => {
  if (e.target.value.length <= max) onChange(field, e.target.value);
};

const dateChange = (onChange, field) => (newValue) => {
  const formattedDate =
    newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd") : "";
  onChange(field, formattedDate);
};

const numericField = (disabled, value) => {
  const showDash = disabled && isEmptyNumeric(value);
  return {
    type: showDash ? "text" : "number",
    value: showDash ? "-" : (value ?? ""),
  };
};

const capitalizedOrDash = (disabled, value) => {
  if (!disabled) return value || "";
  return value ? capitalizeFirstLetter(value) : "-";
};

const isDisabledAndEmpty = (disabled, value) => disabled && !value;

const PrescriptionDateField = ({
  disabled,
  value,
  onChange,
  field,
  error,
  helperText,
}) => {
  if (isDisabledAndEmpty(disabled, value)) {
    return (
      <TextField
        fullWidth
        size="small"
        value="-"
        disabled
        sx={textFieldSx(disabled)}
      />
    );
  }
  return (
    <DesktopDatePicker
      disabled={disabled}
      value={parseDateString(value)}
      onChange={dateChange(onChange, field)}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          placeholder: "MM/DD/YYYY",
          error,
          helperText,
          sx: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
              borderRadius: "8px",
              height: "43px",
              "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              color: disabled ? "#999893" : "#0e1324",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#999893",
              fontSize: "14px",
            },
          },
        },
      }}
    />
  );
};

const MedicationPrescribed = ({ data, onChange, disabled, errors = {} }) => {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mb: 1 }}
      >
        Medication Prescribed
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>RX Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter RX Number"
            value={dashIfEmpty(disabled, data.rxNumber)}
            disabled={disabled}
            onChange={limitedChange(onChange, "rxNumber", 20)}
            slotProps={counterSlotProps(disabled, data.rxNumber, 20)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Written Date</Typography>
          <PrescriptionDateField
            disabled={disabled}
            value={data.writtenDate}
            onChange={onChange}
            field="writtenDate"
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            Drug Name <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Drug Name"
            value={capitalizedOrDash(disabled, data.drugName)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.drugName}
            helperText={errors.drugName}
            onChange={limitedChange(onChange, "drugName", 128)}
            slotProps={counterSlotProps(disabled, data.drugName, 128)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            GPI (Generic Product Identifier){" "}
            <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter GPI"
            value={dashIfEmpty(disabled, data.gpi)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.gpi}
            helperText={errors.gpi}
            onChange={limitedChange(onChange, "gpi", 14)}
            slotProps={counterSlotProps(disabled, data.gpi, 14)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>NDC (National Drug Code)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter NDC"
            value={dashIfEmpty(disabled, data.ndc)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.ndc}
            helperText={errors.ndc}
            onChange={limitedChange(onChange, "ndc", 11)}
            slotProps={counterSlotProps(disabled, data.ndc, 11)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Strength</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Strength"
            value={dashIfEmpty(disabled, data.strength)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.strength}
            helperText={errors.strength}
            onChange={limitedChange(onChange, "strength", 16)}
            slotProps={counterSlotProps(disabled, data.strength, 16)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            Quantity <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            {...numericField(disabled, data.quantity)}
            size="small"
            placeholder="Enter Quantity"
            disabled={disabled} // Apply disabled prop
            error={!!errors.quantity}
            helperText={errors.quantity}
            onChange={(e) => onChange("quantity", e.target.value)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Days Supply</Typography>
          <TextField
            fullWidth
            {...numericField(disabled, data.daysSupply)}
            size="small"
            placeholder="Enter Days Supply"
            disabled={disabled} // Apply disabled prop
            error={!!errors.daysSupply}
            helperText={errors.daysSupply}
            onChange={(e) => onChange("daysSupply", e.target.value)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Prior Authorization (PA) Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter PA Number"
            value={dashIfEmpty(disabled, data.priorAuthNumber)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.priorAuthNumber}
            helperText={errors.priorAuthNumber}
            onChange={limitedChange(onChange, "priorAuthNumber", 25)}
            slotProps={counterSlotProps(disabled, data.priorAuthNumber, 25)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>PA Expiration Date</Typography>
          <PrescriptionDateField
            disabled={disabled}
            value={data.priorAuthExpirationDate}
            onChange={onChange}
            field="priorAuthExpirationDate"
            error={!!errors.priorAuthExpirationDate}
            helperText={errors.priorAuthExpirationDate}
          />
        </Box>
      </Box>
    </Box>
  );
};

MedicationPrescribed.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool, // Add disabled to propTypes
  errors: PropTypes.object,
};

export default MedicationPrescribed;
